import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const API = '/partner/billing';

const fmt = (val) =>
  val != null ? `$${parseFloat(val).toFixed(2)}` : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const colors = {
    PENDING: '#f59e0b',
    PAID:    '#10b981',
    OVERDUE: '#ef4444',
  };
  return (
    <span style={{
      background: colors[status] || '#6b7280',
      color: '#fff',
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
    }}>
      {status}
    </span>
  );
};

export default function PartnerBilling() {
  const { t } = useTranslation();
  const [tab, setTab]           = useState('summary');
  const [summary, setSummary]   = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pricing, setPricing]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sumRes, invRes, priceRes] = await Promise.all([
        axiosInstance.get(`${API}/summary`),
        axiosInstance.get(`${API}/invoices`),
        axiosInstance.get(`${API}/pricing`),
      ]);
      setSummary(sumRes.data);
      setInvoices(invRes.data);
      setPricing(priceRes.data);
    } catch {
      setError(t('PartnerBilling.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const generateInvoice = async () => {
    setGenLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axiosInstance.post(`${API}/invoices/generate`);
      if (res.data.message) {
        setError(res.data.message);
      } else {
        setSuccess(t('PartnerBilling.invoiceGenerated', { number: res.data.invoiceNumber, amount: fmt(res.data.totalAmount) }));
        load();
      }
    } catch {
      setError(t('PartnerBilling.errorGenerateFailed'));
    } finally {
      setGenLoading(false);
    }
  };

  const tabs = [
    { key: 'summary',  label: t('PartnerBilling.tabUnbilledEvents') },
    { key: 'invoices', label: t('PartnerBilling.tabInvoiceHistory') },
    { key: 'pricing',  label: t('PartnerBilling.tabFrameworkPricing') },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t('PartnerBilling.title')}</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
            {t('PartnerBilling.subtitle')}
          </p>
        </div>
        <button
          onClick={generateInvoice}
          disabled={genLoading}
          style={{
            background: '#7c3aed', color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 20px', cursor: 'pointer',
            fontWeight: 600, fontSize: 14,
          }}
        >
          {genLoading ? t('PartnerBilling.generating') : t('PartnerBilling.generateInvoiceNow')}
        </button>
      </div>

      {error   && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#f0fdf4', color: '#16a34a', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{success}</div>}

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          <StatCard label={t('PartnerBilling.statUnbilledEvents')}    value={summary.eventCount}    />
          <StatCard label={t('PartnerBilling.statUnbilledTotal')}     value={fmt(summary.unbilledTotal)} highlight />
          <StatCard label={t('PartnerBilling.statNextBillingDate')}  value={summary.nextBillingDate} />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #e5e7eb', marginBottom: 20 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: 14,
              background: 'none', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#7c3aed' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #7c3aed' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>{t('PartnerBilling.loading')}</div>}

      {!loading && tab === 'summary' && (
        <EventsTable events={summary?.unbilledEvents || []} />
      )}

      {!loading && tab === 'invoices' && (
        <InvoicesTable invoices={invoices} />
      )}

      {!loading && tab === 'pricing' && (
        <PricingTable pricing={pricing} />
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: '16px 20px',
    }}>
      <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6, color: highlight ? '#7c3aed' : '#111827' }}>{value}</div>
    </div>
  );
}

function EventsTable({ events }) {
  const { t } = useTranslation();
  if (!events.length)
    return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>{t('PartnerBilling.emptyEvents')}</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ background: '#f9fafb' }}>
          {[t('PartnerBilling.colClientOrg'), t('PartnerBilling.colFramework'), t('PartnerBilling.colUnitPrice'), t('PartnerBilling.colQty'), t('PartnerBilling.colLineTotal'), t('PartnerBilling.colDate')].map(h => (
            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {events.map(e => (
          <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '10px 12px' }}>{e.orgName}</td>
            <td style={{ padding: '10px 12px' }}>{e.frameworkName}</td>
            <td style={{ padding: '10px 12px' }}>{fmt(e.unitPrice)}</td>
            <td style={{ padding: '10px 12px' }}>{e.quantity}</td>
            <td style={{ padding: '10px 12px', fontWeight: 600 }}>{fmt(e.lineTotal)}</td>
            <td style={{ padding: '10px 12px', color: '#6b7280' }}>{fmtDate(e.eventDate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvoicesTable({ invoices }) {
  const { t } = useTranslation();
  if (!invoices.length)
    return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>{t('PartnerBilling.emptyInvoices')}</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ background: '#f9fafb' }}>
          {[t('PartnerBilling.colInvoiceNumber'), t('PartnerBilling.colDescription'), t('PartnerBilling.colAmount'), t('PartnerBilling.colStatus'), t('PartnerBilling.colDueDate'), t('PartnerBilling.colCreated')].map(h => (
            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {invoices.map(inv => (
          <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#7c3aed' }}>{inv.invoiceNumber}</td>
            <td style={{ padding: '10px 12px', color: '#6b7280', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.description}</td>
            <td style={{ padding: '10px 12px', fontWeight: 600 }}>{fmt(inv.totalAmount)}</td>
            <td style={{ padding: '10px 12px' }}><StatusBadge status={inv.status} /></td>
            <td style={{ padding: '10px 12px', color: '#6b7280' }}>{fmtDate(inv.dueDate)}</td>
            <td style={{ padding: '10px 12px', color: '#6b7280' }}>{fmtDate(inv.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PricingTable({ pricing }) {
  const { t } = useTranslation();
  if (!pricing.length)
    return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>{t('PartnerBilling.emptyPricing')}</p>;
  return (
    <>
      <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
        {t('PartnerBilling.pricingNote')}
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            {[t('PartnerBilling.colFramework'), t('PartnerBilling.colMonthlyPrice')].map(h => (
              <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pricing.map(p => (
            <tr key={p.frameworkId} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 12px' }}>{p.frameworkName}</td>
              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{fmt(p.monthlyPrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
